import { apiTokenRequest, msalInstance } from './msalConfig';
import { isInteractionRequiredError, requestReauth } from './authSession';
import { checkVersionFromResponse } from './versionCheck';

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string) ?? '';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function getAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    // No account at all — fully signed out. Route through the same reauth flow.
    requestReauth();
    throw new ApiError(401, 'no_account', 'No active MSAL account');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (err) {
    // Silent renewal failed. Only the failures that genuinely require an
    // interactive sign-in route through the re-auth prompt — see
    // isInteractionRequiredError: an expired SPA refresh token (24h cap), a
    // Conditional Access re-challenge, or a blocked/timed-out silent iframe
    // (common on iPad Safari/ITP). Network failures are explicitly NOT in that
    // set even though MSAL reports them as BrowserAuthError too, so a tech
    // working offline is never pushed toward a sign-in page that can't load.
    // Everything else rethrows, so a brief blip isn't treated as a dead session.
    if (isInteractionRequiredError(err)) {
      // Hand off to the session-continuity layer: stash where the user is and
      // prompt for re-auth (or redirect) rather than bouncing abruptly here.
      requestReauth();
      // The request that hit this can't proceed; this throw keeps types clean.
      throw new ApiError(401, 'interaction_required', 'Sign-in required');
    }
    throw err;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  try {
    return await handleResponse<T>(res);
  } catch (err) {
    // Telemetry on API failures, with a guard against recursing on the
    // telemetry endpoint itself. We dynamic-import so a circular module
    // graph (telemetry -> apiClient -> telemetry) doesn't break ESM init.
    if (!path.startsWith('/api/event-logs')) {
      void import('./telemetry').then(({ logEvent }) => {
        const status = err instanceof ApiError ? err.status : 0;
        const code = err instanceof ApiError ? err.code : 'fetch_failed';
        const message = err instanceof Error ? err.message : String(err);
        logEvent('client_api_error', `${method} ${path} -> ${status || 'no_response'}`, {
          severity: status >= 500 || status === 0 ? 'error' : 'warning',
          metadata: { method, path, status, code, message },
        });
      }).catch(() => {});
    }
    throw err;
  }
}

// Servers SHOULD return `{ error: string, message: string }` on failures, but in
// practice middleware, proxies, or App Service can hand us non-string fields (or
// no body at all). Coerce defensively so ApiError.message is always a real
// string — passing a non-string into `super(message)` makes `err.message` come
// back as the literal "[object Object]", which destroys diagnostics downstream.
function coerceErrorField(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return fallback;
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  // Cheap drift check on every response — server stamps X-App-Version, and
  // if our bundle is older we'll force-reload once. Runs before throwing on
  // errors so a 4xx still gets the version compared.
  try {
    checkVersionFromResponse(res.headers);
  } catch {
    /* version check must never break a successful response */
  }
  if (!res.ok) {
    // A server-side 401 means our token was rejected (expired/revoked) even
    // though the client thought it was valid — funnel it through the same
    // session-continuity flow so the user gets the friendly re-auth prompt and
    // returns to where they were.
    if (res.status === 401) requestReauth();
    let payload: { error?: unknown; message?: unknown } = {};
    try {
      payload = await res.json();
    } catch {
      // empty body is fine
    }
    throw new ApiError(
      res.status,
      coerceErrorField(payload.error, 'http_error'),
      coerceErrorField(payload.message, res.statusText),
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface UploadResult {
  container: string;
  path: string;
  url: string;
  size: number;
  contentType: string;
}

/**
 * Multipart POST to /api/uploads/:container. The browser sets the Content-Type
 * header (with boundary) automatically when the body is a FormData.
 */
async function upload(
  container: string,
  file: File | Blob,
  options: { path?: string; filename?: string } = {},
): Promise<UploadResult> {
  const token = await getAccessToken();
  const form = new FormData();
  if (options.path) form.append('path', options.path);
  const filename = options.filename
    ?? (file instanceof File ? file.name : 'upload.bin');
  form.append('file', file, filename);

  const res = await fetch(`${baseUrl}/api/uploads/${encodeURIComponent(container)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  return handleResponse<UploadResult>(res);
}

/**
 * GET an endpoint that returns a binary body (e.g. /api/reports/:id/pdf).
 * Returns the Blob plus the filename parsed out of Content-Disposition (or null).
 */
async function getBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = await getAccessToken();
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    if (res.status === 401) requestReauth();
    let payload: { error?: unknown; message?: unknown } = {};
    try {
      payload = await res.json();
    } catch {
      // empty body is fine
    }
    throw new ApiError(
      res.status,
      coerceErrorField(payload.error, 'http_error'),
      coerceErrorField(payload.message, res.statusText),
    );
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return { blob: await res.blob(), filename: m?.[1] ?? null };
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload,
  getBlob,
};
